import { RefreshCw, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '@/api';
import { ALL_LOCK_TYPES, type LegalLock, type LegalLockQuery, type LockType } from '@/api/areas/market';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useApi } from '@/lib/useApi';
import { EnumBadge, SelectField, useEnumLabel, useEnumOptions, When } from '../../compliance/ui';
import { ABUSE_STATUS, DISPUTE_STATUS } from '../../compliance/vocab';
import { LOCK_TYPE } from '../shared';
import { SystemReason } from '@/components/SystemReason';
import { policyLabel } from '@/lib/labels';

type LockState = NonNullable<LegalLockQuery['state']>;
const STATES: LockState[] = ['active', 'released', 'all'];

/** `/compliance/legal-locks`: court, UDRP, registry and compliance locks on domains (`domain.domain_locks`, spec §45). */
export function LegalLocksPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  useDocumentTitle(t('locks.title'));
  const [params, setParams] = useSearchParams();
  const state = (params.get('state') ?? 'active') as LockState;
  const lockType = (params.get('type') ?? '') as LockType | '';
  const domain = params.get('domain') ?? '';
  const result = useApi(
    (signal) =>
      api.listLegalLocks(
        {
          state,
          lock_type: lockType || undefined,
          domain: domain || undefined,
          limit: 200,
        },
        { signal },
      ),
    [state, lockType, domain],
  );
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const effects = result.status === 'success' ? result.data.effects : undefined;
  const filtered = Boolean(params.get('state') || lockType || domain);

  const columns: Column<LegalLock>[] = [
    {
      key: 'domain',
      header: t('locks.col.domain'),
      cell: (l) => (
        <EntityRef
          label={l.unicode_name || l.domain_name}
          to={`/domains/${encodeURIComponent(l.domain_name)}`}
          id={l.domain_id}
          sub={label('lifecycle', l.lifecycle_state)}
        />
      ),
    },
    {
      key: 'lock',
      header: t('locks.col.lock'),
      cell: (l) => (
        <div className="space-y-1">
          <EntityRef label={label('lockType', l.lock_type)} to={`/compliance/legal-locks/${encodeURIComponent(l.id)}`} id={l.id} />
          {l.active ? <Badge tone="pending">{t('locks.active')}</Badge> : <Badge tone="neutral">{t('locks.released')}</Badge>}
        </div>
      ),
    },
    {
      key: 'reason',
      // The case reference is already in the "placed for" column, so it is not repeated here.
      header: t('locks.col.reason'),
      cell: (l) => (
        <div className="text-sm">
          <SystemReason reason={l.reason} />
        </div>
      ),
    },
    {
      key: 'for',
      header: t('locks.col.placedFor'),
      cell: (l) => (
        <div className="space-y-1 text-sm">
          {l.dispute ? (
            <>
              <EntityRef
                label={`${label('disputeType', l.dispute.dispute_type)} ${l.dispute.case_reference}`}
                to={`/compliance/icann/disputes/${encodeURIComponent(l.dispute.id)}`}
                id={l.dispute.id}
                sub={l.dispute.provider}
              />{' '}
              <EnumBadge group="disputeStatus" value={l.dispute.status} tones={DISPUTE_STATUS} />
            </>
          ) : l.abuse_case ? (
            <>
              <EntityRef
                label={l.abuse_case.case_number}
                to={`/compliance/icann/abuse/${encodeURIComponent(l.abuse_case.id)}`}
                id={l.abuse_case.id}
                sub={t('locks.domainAction', { action: label('domainAction', l.abuse_case.action) })}
              />{' '}
              <EnumBadge group="abuseStatus" value={l.abuse_case.status} tones={ABUSE_STATUS} />
            </>
          ) : (
            <span className="text-muted-foreground">{t('locks.noCase')}</span>
          )}
        </div>
      ),
    },
    {
      key: 'applied',
      header: t('locks.col.applied'),
      cell: (l) => (
        <div className="text-sm">
          <When at={l.applied_at} />
          <div className="text-xs text-muted-foreground">
            <EntityRef label={l.applied_by_display_name ?? label('actorType', l.applied_by_actor_type)} id={l.applied_by_actor_id} />
          </div>
        </div>
      ),
    },
    {
      key: 'released',
      header: t('locks.col.released'),
      cell: (l) =>
        l.released_at ? (
          <div className="space-y-0.5 text-sm">
            <When at={l.released_at} />
            <div className="text-xs text-muted-foreground">
              <EntityRef label={l.released_by_display_name ?? label('actorType', l.released_by_actor_type ?? '')} id={l.released_by_actor_id} />
            </div>
            {l.release_reason ? <div className="text-xs text-muted-foreground"><SystemReason reason={l.release_reason} /></div> : null}
            {l.approval_request_id ? (
              <div className="text-xs">
                <Link to="/admin/approvals" className="text-primary hover:underline">
                  {t('locks.approval')}
                </Link>
              </div>
            ) : null}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('locks.title')}
        crumbs={[{ label: t('crumbs.compliance') }]}
        subtitle={t('locks.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        {effects === null ? (
          <Callout tone="crit" title={t('locks.noEffectsTitle')}>
            {t('locks.noEffects')}
          </Callout>
        ) : null}
        <Callout tone="info">{t('locks.unlockHint')}</Callout>
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              filtered ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">{t('locks.state')}</span>
              <ToggleGroup type="single" variant="outline" value={state} onValueChange={(v) => v && set('state', v === 'active' ? '' : v)} aria-label={t('locks.state')}>
                {STATES.map((s) => (
                  <ToggleGroupItem key={s} value={s} className="px-3">
                    {label('lockState', s)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
            <SelectField
              label={t('locks.type')}
              value={lockType}
              allLabel={t('locks.allLegal')}
              options={options('lockType', ALL_LOCK_TYPES)}
              onChange={(v) => set('type', v)}
              className="w-full sm:w-72"
            />
            <Field label={t('common.domain')} className="w-full sm:w-56">
              <Input
                key={domain}
                type="search"
                defaultValue={domain}
                onKeyDown={(e) => e.key === 'Enter' && set('domain', e.currentTarget.value.trim())}
                onBlur={(e) => set('domain', e.target.value.trim())}
                placeholder={t('common.exactName')}
              />
            </Field>
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={4} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('locks.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? <DataTable columns={columns} rows={result.data.items} rowKey={(l) => l.id} empty={<EmptyState title={t('locks.empty')} />} /> : null}
        </Section>
        {effects ? (
          <Section
            title={t('locks.effects')}
            description={
              <Link to={`/compliance/policies/${encodeURIComponent(effects.policy_key)}`} className="text-primary hover:underline">
                {t('locks.effectsHint', { policy: policyLabel(effects.policy_key, effects.version) })}
              </Link>
            }
            flush
          >
            <DataTable
              columns={[
                {
                  key: 'lock',
                  header: t('locks.ecol.lock'),
                  cell: (lt: LockType) => <EnumBadge group="lockType" value={lt} tones={LOCK_TYPE} />,
                },
                {
                  key: 'blocked',
                  header: t('locks.ecol.blocked'),
                  cell: (lt: LockType) => (
                    <span className="text-sm">
                      {(effects.blocks[lt] ?? []).map((op) => label('operation', op)).join(', ') || <span className="text-muted-foreground">{t('locks.nothing')}</span>}
                    </span>
                  ),
                },
              ]}
              rows={ALL_LOCK_TYPES}
              rowKey={(lt) => lt}
            />
          </Section>
        ) : null}
      </div>
    </>
  );
}
